// src/cockpit4/components/UserPersona.tsx
// ============================================
// P1-功能5：用户画像与智能分群（营销中心）
//
// 功能：
//   1. 基于营销数据自动构建用户画像（活跃度/互动质量/流失风险评分）
//   2. 智能用户分群（高价值 KOC / 活跃 / 普通 / 沉睡 / 流失风险）
//   3. 每个群体的特征分析（特征描述 + 标签）
//   4. 精准营销策略推荐（内置规则库，按分群匹配行动建议）
//   5. 个性化内容方向推荐
//   6. 平台维度的画像差异对比
//   7. 分群阈值配置持久化到 localStorage，支持自定义调优
//
// 数据来源：
//   - useDashboardData hook 的 marketing.platforms（Record<MarketingPlatform, PlatformData>）
//   - PlatformData.metrics: views / likes / favorites / comments / shares / dm / followersGain / followersTotal
//   - 画像评分为基于聚合营销指标的估算逻辑（非用户级数据），用于辅助营销决策
//   - 分群阈值配置存 localStorage（key: xeaos_marketing_user_segments）
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserCircle,
  Crown,
  Heart,
  AlertTriangle,
  TrendingUp,
  Target,
  RefreshCw,
  Loader2,
  Settings,
  Sparkles,
  BarChart3,
  CheckCircle2,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";
import type {
  MarketingPlatform,
  PlatformData,
  PlatformMetrics,
} from "../../hooks/useDashboardData";

// ============================================
// 类型定义
// ============================================

/** 分群类型 */
type SegmentType = "koc" | "active" | "normal" | "dormant" | "churn_risk";

/** 用户画像评分（基于聚合营销指标估算） */
interface SegmentScores {
  /** 活跃度评分 0-1：(likes + comments + shares) / max(views, 1) */
  activity: number;
  /** 互动质量评分 0-1：(favorites + dm) / max(likes + comments, 1) */
  interaction: number;
  /** 流失风险评分 0-100：100 - (followersGain / max(followersTotal, 1)) * 100 */
  churnRisk: number;
}

/** 分群阈值配置（可自定义，持久化到 localStorage） */
interface SegmentConfig {
  /** KOC 互动质量门槛 */
  kocInteraction: number;
  /** KOC 活跃度门槛 */
  kocActivity: number;
  /** 活跃用户活跃度门槛 */
  activeActivity: number;
  /** 活跃用户互动质量门槛 */
  activeInteraction: number;
  /** 普通用户活跃度门槛 */
  normalActivity: number;
  /** 流失风险评分门槛 */
  churnRiskThreshold: number;
}

/** 单个分群的汇总信息 */
interface SegmentSummary {
  type: SegmentType;
  /** 人数（估算） */
  count: number;
  /** 占比 0-100 */
  ratio: number;
  /** 该群体平均活跃度 */
  avgActivity: number;
  /** 该群体平均互动质量 */
  avgInteraction: number;
  /** 该群体平均流失风险 */
  avgChurnRisk: number;
}

/** 单平台的画像快照 */
interface PlatformPersona {
  platform: MarketingPlatform;
  /** 总用户数（粉丝数） */
  followers: number;
  scores: SegmentScores;
  /** 各分群估算人数 */
  distribution: Record<SegmentType, number>;
  /** 主导分群（人数最多的分群） */
  dominantSegment: SegmentType;
}

/** 营销策略（内置规则库） */
interface Strategy {
  title: string;
  actions: string[];
  contentDirection: string;
}

// ============================================
// 常量与规则库
// ============================================

const SEGMENTS_STORAGE_KEY = "xeaos_marketing_user_segments";

/** 默认分群阈值配置（来自改进计划 v2.0 规则） */
const DEFAULT_SEGMENT_CONFIG: SegmentConfig = {
  kocInteraction: 0.3,
  kocActivity: 0.15,
  activeActivity: 0.1,
  activeInteraction: 0.15,
  normalActivity: 0.05,
  churnRiskThreshold: 70,
};

/** 平台元信息（标签 + 主题色） */
const PLATFORM_META: Record<MarketingPlatform, { label: string; color: string }> = {
  wechat_official: { label: "公众号", color: "#10B981" },
  xiaohongshu: { label: "小红书", color: "#EF4444" },
  douyin_enterprise: { label: "抖音企业号", color: "#22D3EE" },
  douyin_personal: { label: "抖音个人号", color: "#3B82F6" },
  zhihu: { label: "知乎", color: "#60A5FA" },
  bilibili: { label: "B站", color: "#FB7299" },
};

/** 分群元信息（标签 / 颜色 / 图标 / 特征描述 / 标签集） */
const SEGMENT_META: Record<
  SegmentType,
  {
    label: string;
    shortLabel: string;
    color: string;
    icon: typeof Crown;
    description: string;
    tags: string[];
  }
> = {
  koc: {
    label: "高价值 KOC",
    shortLabel: "KOC",
    color: "#FBBF24",
    icon: Crown,
    description:
      "高互动质量且高活跃度的核心用户，是品牌传播的关键意见消费者。他们深度参与内容互动，具有较强的传播影响力与 UGC 创作潜力。",
    tags: ["深度互动", "高传播力", "品牌忠诚", "UGC 潜力"],
  },
  active: {
    label: "活跃用户",
    shortLabel: "活跃",
    color: "#34D399",
    icon: Heart,
    description:
      "持续关注并积极参与互动的用户，是日常运营的核心群体。他们对品牌内容保持热度，转化潜力较高，适合引导跨平台关注。",
    tags: ["持续互动", "内容偏好明确", "易转化", "多平台活跃"],
  },
  normal: {
    label: "普通用户",
    shortLabel: "普通",
    color: "#38BDF8",
    icon: Users,
    description:
      "有基础互动但深度不足的用户，占据粉丝主体。通过内容优化和引导可逐步提升至活跃层级，是培育型群体。",
    tags: ["浅层互动", "内容浏览为主", "提升空间大", "需培育"],
  },
  dormant: {
    label: "沉睡用户",
    shortLabel: "沉睡",
    color: "#9CA3AF",
    icon: UserCircle,
    description:
      "长期未互动的粉丝用户，可能对现有内容失去兴趣。需要通过召回策略与重磅内容重新激活，避免进一步流失。",
    tags: ["长期沉默", "内容不匹配", "需唤醒", "流失边缘"],
  },
  churn_risk: {
    label: "流失风险",
    shortLabel: "流失风险",
    color: "#F87171",
    icon: AlertTriangle,
    description:
      "粉丝增长停滞、有流失倾向的用户群体。需要及时介入关怀，了解流失原因并针对性挽回，防止用户彻底流失。",
    tags: ["增长停滞", "流失倾向", "需关怀", "紧急挽回"],
  },
};

/** 分群展示顺序 */
const SEGMENT_ORDER: SegmentType[] = ["koc", "active", "normal", "dormant", "churn_risk"];

/**
 * 内置营销策略规则库（非用户数据，按分群类型匹配推荐策略）
 * 每个分群对应一套精准营销行动建议与内容方向
 */
const SEGMENT_STRATEGIES: Record<SegmentType, Strategy> = {
  koc: {
    title: "KOC 培育计划",
    actions: [
      "邀请参与新品内测",
      "提供专属折扣码",
      "建立深度合作关系",
      "邀请创作 UGC 内容",
    ],
    contentDirection: "深度产品故事、用户见证、共创内容",
  },
  active: {
    title: "活跃用户运营",
    actions: [
      "定期互动保持热度",
      "推送个性化内容",
      "邀请参与活动",
      "引导关注其他平台",
    ],
    contentDirection: "热点话题、教程干货、互动话题",
  },
  dormant: {
    title: "沉睡用户唤醒",
    actions: [
      "发送召回推送",
      "提供限时优惠",
      "推送新功能/新内容",
      "降低推送频率避免打扰",
    ],
    contentDirection: "重大更新、重磅活动、回归福利",
  },
  churn_risk: {
    title: "流失风险挽回",
    actions: [
      "一对一关怀沟通",
      "了解流失原因",
      "提供专属福利",
      "调整内容方向满足需求",
    ],
    contentDirection: "用户调研、改进说明、诚意沟通",
  },
  normal: {
    title: "普通用户提升",
    actions: [
      "推送热门内容吸引关注",
      "引导互动",
      "提供入门价值",
      "循序渐进培育",
    ],
    contentDirection: "热门爆款、入门教程、价值科普",
  },
};

// ============================================
// 工具函数
// ============================================

/** 安全读取分群配置（localStorage） */
function loadSegmentConfig(): SegmentConfig {
  try {
    const raw = localStorage.getItem(SEGMENTS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SEGMENT_CONFIG };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      // 合并默认值，确保新增字段有兜底
      return { ...DEFAULT_SEGMENT_CONFIG, ...parsed };
    }
    return { ...DEFAULT_SEGMENT_CONFIG };
  } catch {
    return { ...DEFAULT_SEGMENT_CONFIG };
  }
}

/** 安全写入分群配置（localStorage） */
function saveSegmentConfig(config: SegmentConfig): void {
  try {
    localStorage.setItem(SEGMENTS_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/**
 * 计算平台画像评分（基于聚合营销指标的估算逻辑）
 * 注意：营销数据为平台级聚合指标，无用户级数据。
 * 以下评分反映平台整体的用户互动质量与流失风险，用于辅助分群决策。
 */
function computeScores(metrics: PlatformMetrics): SegmentScores {
  const views = Math.max(metrics.views, 1);
  const activity = (metrics.likes + metrics.comments + metrics.shares) / views;
  const interaction =
    (metrics.favorites + metrics.dm) / Math.max(metrics.likes + metrics.comments, 1);
  const churnRisk =
    100 - (metrics.followersGain / Math.max(metrics.followersTotal, 1)) * 100;
  return {
    activity: Math.max(0, activity),
    interaction: Math.max(0, interaction),
    churnRisk: Math.max(0, Math.min(100, churnRisk)),
  };
}

/**
 * 估算平台粉丝在各分群中的分布人数
 *
 * 估算逻辑说明（必须注释：这是估算逻辑，非用户级数据）：
 *   营销数据只有平台级聚合指标（views/likes/...），无法获取个体用户行为。
 *   以下根据平台的活跃度/互动质量/流失风险评分，结合分群阈值规则，
 *   估算粉丝在各分群中的比例分布。这是一个估算模型，用于辅助营销决策，
 *   不代表精确的用户级分群结果。
 *
 * 分配策略（分层分配，避免重叠）：
 *   1. KOC：满足阈值时，按互动深度估算占比（上限 12%）
 *   2. 活跃：满足阈值时，按活跃度估算占比（上限 25%，扣除 KOC）
 *   3. 普通：满足阈值时，按活跃度估算占比（上限 30%，扣除 KOC + 活跃）
 *   4. 流失风险：按流失风险评分超出阈值的部分估算占比（上限 30%）
 *   5. 沉睡：剩余粉丝归入沉睡群体
 */
function estimateSegmentDistribution(
  scores: SegmentScores,
  followersTotal: number,
  config: SegmentConfig
): Record<SegmentType, number> {
  const dist: Record<SegmentType, number> = {
    koc: 0,
    active: 0,
    normal: 0,
    dormant: 0,
    churn_risk: 0,
  };

  if (followersTotal <= 0) return dist;

  // KOC：互动质量与活跃度均达标的深度互动用户
  if (scores.interaction > config.kocInteraction && scores.activity > config.kocActivity) {
    dist.koc = Math.min(scores.activity * scores.interaction * 2, 0.12) * followersTotal;
  }

  // 活跃：活跃度与互动质量达标的积极互动用户（扣除 KOC 部分）
  if (scores.activity > config.activeActivity && scores.interaction > config.activeInteraction) {
    const activePool = Math.min(scores.activity * 1.5, 0.25) * followersTotal;
    dist.active = Math.max(0, activePool - dist.koc);
  }

  // 普通：有基础活跃度的用户（扣除 KOC + 活跃）
  if (scores.activity > config.normalActivity) {
    const normalPool = Math.min(scores.activity * 1.2, 0.30) * followersTotal;
    dist.normal = Math.max(0, normalPool - dist.koc - dist.active);
  }

  // 流失风险：流失风险评分超过阈值时，按超出比例估算（上限 30%）
  if (scores.churnRisk > config.churnRiskThreshold) {
    const excess = (scores.churnRisk - config.churnRiskThreshold) / (100 - config.churnRiskThreshold);
    dist.churn_risk = Math.min(excess * 0.3, 0.30) * followersTotal;
  } else {
    // 未达高风险阈值时，仍按风险评分比例估算少量潜在流失用户
    dist.churn_risk = Math.min((scores.churnRisk / 100) * 0.15, 0.15) * followersTotal;
  }

  // 沉睡：剩余粉丝（活跃度极低的平台，大部分粉丝归入沉睡）
  const allocated = dist.koc + dist.active + dist.normal + dist.churn_risk;
  dist.dormant = Math.max(0, followersTotal - allocated);

  return dist;
}

/** 判断主导分群（人数最多的分群） */
function getDominantSegment(dist: Record<SegmentType, number>): SegmentType {
  let max: SegmentType = "dormant";
  let maxCount = -1;
  for (const t of SEGMENT_ORDER) {
    if (dist[t] > maxCount) {
      maxCount = dist[t];
      max = t;
    }
  }
  return max;
}

/** 构建单平台画像快照 */
function buildPlatformPersona(
  platform: MarketingPlatform,
  data: PlatformData,
  config: SegmentConfig
): PlatformPersona {
  const scores = computeScores(data.metrics);
  const distribution = estimateSegmentDistribution(scores, data.metrics.followersTotal, config);
  return {
    platform,
    followers: data.metrics.followersTotal,
    scores,
    distribution,
    dominantSegment: getDominantSegment(distribution),
  };
}

/** 判断平台是否有真实数据（非降级空数据） */
function hasRealData(data: PlatformData): boolean {
  if (data.degraded) return false;
  const m = data.metrics;
  return (
    m.views > 0 ||
    m.likes > 0 ||
    m.favorites > 0 ||
    m.comments > 0 ||
    m.shares > 0 ||
    m.dm > 0 ||
    m.followersGain > 0 ||
    m.followersTotal > 0
  );
}

/** 格式化数字（千分位） */
function formatNumber(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
}

/** 格式化百分比 */
function formatPercent(n: number): string {
  return `${n.toFixed(1)}%`;
}

// ============================================
// 主组件
// ============================================

export default function UserPersona() {
  const dashboard = useDashboardData();
  const [config, setConfig] = useState<SegmentConfig>(DEFAULT_SEGMENT_CONFIG);
  const [selectedPlatform, setSelectedPlatform] = useState<MarketingPlatform | "all">("all");
  const [selectedSegment, setSelectedSegment] = useState<SegmentType | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // 初次挂载：加载本地分群配置
  useEffect(() => {
    setConfig(loadSegmentConfig());
  }, []);

  // 配置变化时持久化
  useEffect(() => {
    saveSegmentConfig(config);
  }, [config]);

  // 刷新触发（标记刷新动画，数据由 hook 自动拉取）
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await dashboard.refresh();
    } finally {
      // 至少展示 400ms 刷新动画，避免闪烁
      setTimeout(() => setRefreshing(false), 400);
    }
  }, [dashboard]);

  // ============================================
  // 派生数据：平台画像 + 分群汇总
  // ============================================

  /** 所有平台的画像快照 */
  const allPersonas = useMemo<PlatformPersona[]>(() => {
    const platforms = Object.keys(dashboard.marketing.platforms) as MarketingPlatform[];
    return platforms
      .map((p) => buildPlatformPersona(p, dashboard.marketing.platforms[p], config))
      .filter((persona) => hasRealData(dashboard.marketing.platforms[persona.platform]));
  }, [dashboard.marketing.platforms, config]);

  /** 当前选中平台的画像快照（all 表示全部） */
  const filteredPersonas = useMemo<PlatformPersona[]>(() => {
    if (selectedPlatform === "all") return allPersonas;
    return allPersonas.filter((p) => p.platform === selectedPlatform);
  }, [allPersonas, selectedPlatform]);

  /** 整体画像概览（基于选中平台聚合） */
  const overview = useMemo(() => {
    const totalUsers = filteredPersonas.reduce((s, p) => s + p.followers, 0);
    // 加权平均（按粉丝数加权，反映"平均用户体验"）
    const weightedActivity =
      totalUsers > 0
        ? filteredPersonas.reduce((s, p) => s + p.scores.activity * p.followers, 0) / totalUsers
        : 0;
    const weightedInteraction =
      totalUsers > 0
        ? filteredPersonas.reduce((s, p) => s + p.scores.interaction * p.followers, 0) / totalUsers
        : 0;
    const churnRiskUsers = filteredPersonas.reduce((s, p) => s + p.distribution.churn_risk, 0);
    return {
      totalUsers,
      avgActivity: weightedActivity,
      avgInteraction: weightedInteraction,
      churnRiskUsers,
    };
  }, [filteredPersonas]);

  /** 分群汇总（聚合各平台的分布人数） */
  const segmentSummaries = useMemo<SegmentSummary[]>(() => {
    // 聚合各分群的总人数
    const aggregated: Record<SegmentType, { count: number; activitySum: number; interactionSum: number; churnSum: number; weight: number }> = {
      koc: { count: 0, activitySum: 0, interactionSum: 0, churnSum: 0, weight: 0 },
      active: { count: 0, activitySum: 0, interactionSum: 0, churnSum: 0, weight: 0 },
      normal: { count: 0, activitySum: 0, interactionSum: 0, churnSum: 0, weight: 0 },
      dormant: { count: 0, activitySum: 0, interactionSum: 0, churnSum: 0, weight: 0 },
      churn_risk: { count: 0, activitySum: 0, interactionSum: 0, churnSum: 0, weight: 0 },
    };

    for (const persona of filteredPersonas) {
      for (const t of SEGMENT_ORDER) {
        const cnt = persona.distribution[t];
        const a = aggregated[t];
        a.count += cnt;
        // 按该分群人数加权累计评分（估算该分群用户的平均画像）
        a.activitySum += persona.scores.activity * cnt;
        a.interactionSum += persona.scores.interaction * cnt;
        a.churnSum += persona.scores.churnRisk * cnt;
        a.weight += cnt;
      }
    }

    const total = overview.totalUsers;
    return SEGMENT_ORDER.map((t) => {
      const a = aggregated[t];
      return {
        type: t,
        count: a.count,
        ratio: total > 0 ? (a.count / total) * 100 : 0,
        avgActivity: a.weight > 0 ? a.activitySum / a.weight : 0,
        avgInteraction: a.weight > 0 ? a.interactionSum / a.weight : 0,
        avgChurnRisk: a.weight > 0 ? a.churnSum / a.weight : 0,
      };
    });
  }, [filteredPersonas, overview.totalUsers]);

  /** 是否处于空数据状态 */
  const isEmpty = allPersonas.length === 0 && !dashboard.loading;

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-amber-500/15 border border-amber-500/30">
            <UserCircle size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">用户画像与智能分群</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              User Persona · {formatNumber(overview.totalUsers)} 用户 · {filteredPersonas.length} 平台
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* 平台选择 */}
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value as MarketingPlatform | "all")}
            className="px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300 focus:outline-none focus:border-amber-500/40 cursor-pointer"
          >
            <option value="all">全平台</option>
            {(Object.keys(PLATFORM_META) as MarketingPlatform[]).map((p) => (
              <option key={p} value={p}>
                {PLATFORM_META[p].label}
              </option>
            ))}
          </select>
          {/* 设置按钮 */}
          <button
            onClick={() => setShowSettings((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
              showSettings
                ? "bg-amber-500/25 border-amber-500/40"
                : "bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]"
            }`}
          >
            <Settings size={11} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">阈值</span>
          </button>
          {/* 刷新按钮 */}
          <button
            onClick={handleRefresh}
            disabled={refreshing || dashboard.loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing || dashboard.loading ? (
              <Loader2 size={11} className="text-amber-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-amber-400" />
            )}
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">
              {refreshing ? "刷新中..." : "刷新"}
            </span>
          </button>
        </div>
      </div>

      {/* 阈值设置面板（可折叠） */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="p-4 space-y-2 bg-white/[0.01]">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono text-amber-400 uppercase tracking-wider">
                  分群阈值配置（持久化到 localStorage）
                </div>
                <button
                  onClick={() => {
                    setConfig({ ...DEFAULT_SEGMENT_CONFIG });
                  }}
                  className="px-2 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200 border border-white/[0.06]"
                >
                  恢复默认
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <ThresholdInput
                  label="KOC 互动质量"
                  value={config.kocInteraction}
                  step={0.01}
                  onChange={(v) => setConfig((s) => ({ ...s, kocInteraction: v }))}
                />
                <ThresholdInput
                  label="KOC 活跃度"
                  value={config.kocActivity}
                  step={0.01}
                  onChange={(v) => setConfig((s) => ({ ...s, kocActivity: v }))}
                />
                <ThresholdInput
                  label="流失风险门槛"
                  value={config.churnRiskThreshold}
                  step={1}
                  onChange={(v) => setConfig((s) => ({ ...s, churnRiskThreshold: v }))}
                />
                <ThresholdInput
                  label="活跃 活跃度"
                  value={config.activeActivity}
                  step={0.01}
                  onChange={(v) => setConfig((s) => ({ ...s, activeActivity: v }))}
                />
                <ThresholdInput
                  label="活跃 互动质量"
                  value={config.activeInteraction}
                  step={0.01}
                  onChange={(v) => setConfig((s) => ({ ...s, activeInteraction: v }))}
                />
                <ThresholdInput
                  label="普通 活跃度"
                  value={config.normalActivity}
                  step={0.01}
                  onChange={(v) => setConfig((s) => ({ ...s, normalActivity: v }))}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {dashboard.loading && allPersonas.length === 0 ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-amber-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载营销数据...</p>
        </div>
      ) : isEmpty ? (
        /* 空状态：无营销数据 */
        <div className="py-12 text-center px-4">
          <Users size={28} className="mx-auto text-gray-600 mb-3" />
          <p className="text-[12px] font-mono text-gray-500">暂无营销数据，无法构建用户画像</p>
          <p className="text-[10px] font-mono text-gray-600 mt-1">
            请检查 ContentRuntime 数据接入配置，确保至少一个平台有真实指标数据
          </p>
        </div>
      ) : (
        <>
          {/* 整体画像概览 KPI */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <OverviewKpi
              icon={Users}
              label="总用户数"
              value={formatNumber(overview.totalUsers)}
              color="#38BDF8"
            />
            <OverviewKpi
              icon={TrendingUp}
              label="平均活跃度"
              value={overview.avgActivity.toFixed(3)}
              color="#34D399"
            />
            <OverviewKpi
              icon={Target}
              label="平均互动质量"
              value={overview.avgInteraction.toFixed(3)}
              color="#A78BFA"
            />
            <OverviewKpi
              icon={AlertTriangle}
              label="流失风险用户"
              value={formatNumber(overview.churnRiskUsers)}
              color="#F87171"
            />
          </div>

          {/* 分群卡片网格 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={11} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                智能分群（基于评分估算，点击查看策略）
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {segmentSummaries.map((seg) => {
                const meta = SEGMENT_META[seg.type];
                const isSelected = selectedSegment === seg.type;
                return (
                  <button
                    key={seg.type}
                    onClick={() => setSelectedSegment(isSelected ? null : seg.type)}
                    className={`rounded-lg border p-3 text-left transition-all ${
                      isSelected
                        ? "border-amber-500/50 bg-amber-500/[0.06]"
                        : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                    }`}
                    style={isSelected ? { boxShadow: `0 0 0 1px ${meta.color}40` } : undefined}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <meta.icon size={13} style={{ color: meta.color }} />
                      {isSelected && <CheckCircle2 size={11} style={{ color: meta.color }} />}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider truncate">
                      {meta.shortLabel}
                    </div>
                    <div className="text-[18px] font-mono font-bold mt-0.5" style={{ color: meta.color }}>
                      {formatNumber(seg.count)}
                    </div>
                    <div className="text-[9px] font-mono text-gray-500 mt-0.5">
                      占比 {formatPercent(seg.ratio)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 选中分群的详细分析 */}
          <AnimatePresence>
            {selectedSegment && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden border-b border-white/[0.04]"
              >
                <SegmentDetail
                  summary={segmentSummaries.find((s) => s.type === selectedSegment)!}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* 平台维度画像差异对比 */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                平台维度画像对比
              </span>
            </div>
            {allPersonas.length === 0 ? (
              <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                暂无平台数据可供对比
              </p>
            ) : (
              <div className="space-y-1.5">
                {allPersonas.map((persona) => (
                  <PlatformCompareRow key={persona.platform} persona={persona} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================
// 概览 KPI 小卡片
// ============================================
interface OverviewKpiProps {
  icon: typeof Users;
  label: string;
  value: string;
  color: string;
}

function OverviewKpi({ icon: Icon, label, value, color }: OverviewKpiProps) {
  return (
    <div
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={14} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="text-[14px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 阈值输入框
// ============================================
interface ThresholdInputProps {
  label: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
}

function ThresholdInput({ label, value, step, onChange }: ThresholdInputProps) {
  return (
    <div>
      <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider block mb-1">
        {label}
      </label>
      <input
        type="number"
        value={value}
        step={step}
        min={0}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="w-full px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-amber-500/40"
      />
    </div>
  );
}

// ============================================
// 选中分群详情面板
// ============================================
interface SegmentDetailProps {
  summary: SegmentSummary;
}

function SegmentDetail({ summary }: SegmentDetailProps) {
  const meta = SEGMENT_META[summary.type];
  const strategy = SEGMENT_STRATEGIES[summary.type];

  return (
    <div className="p-4 bg-white/[0.01]">
      {/* 分群标题与特征描述 */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}
        >
          <meta.icon size={16} style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-white">{meta.label}</span>
            <span className="text-[10px] font-mono text-gray-500">
              {formatNumber(summary.count)} 人 · 占比 {formatPercent(summary.ratio)}
            </span>
          </div>
          <p className="text-[10px] font-mono text-gray-400 mt-1 leading-relaxed">
            {meta.description}
          </p>
        </div>
      </div>

      {/* 特征标签 */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {meta.tags.map((tag) => (
          <span
            key={tag}
            className="px-1.5 py-0.5 rounded text-[9px] font-mono"
            style={{ background: `${meta.color}15`, color: meta.color }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* 评分指标 */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <ScoreBar label="平均活跃度" value={summary.avgActivity} max={1} color="#34D399" />
        <ScoreBar label="平均互动质量" value={summary.avgInteraction} max={1} color="#A78BFA" />
        <ScoreBar label="平均流失风险" value={summary.avgChurnRisk} max={100} color="#F87171" />
      </div>

      {/* 推荐营销策略 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 mb-2">
        <div className="flex items-center gap-1.5 mb-2">
          <Target size={11} style={{ color: meta.color }} />
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
            推荐营销策略 · {strategy.title}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {strategy.actions.map((action, i) => (
            <div
              key={action}
              className="flex items-start gap-1.5 text-[10px] font-mono text-gray-300"
            >
              <span
                className="flex-shrink-0 w-3.5 h-3.5 rounded flex items-center justify-center text-[8px]"
                style={{ background: `${meta.color}20`, color: meta.color }}
              >
                {i + 1}
              </span>
              <span className="leading-snug">{action}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 推荐内容方向 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles size={11} style={{ color: meta.color }} />
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
            推荐内容方向
          </span>
        </div>
        <p className="text-[11px] font-mono text-gray-300 leading-relaxed">
          {strategy.contentDirection}
        </p>
      </div>
    </div>
  );
}

// ============================================
// 评分进度条
// ============================================
interface ScoreBarProps {
  label: string;
  value: number;
  max: number;
  color: string;
}

function ScoreBar({ label, value, max, color }: ScoreBarProps) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </span>
        <span className="text-[10px] font-mono font-semibold" style={{ color }}>
          {value.toFixed(2)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4 }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}80, ${color})` }}
        />
      </div>
    </div>
  );
}

// ============================================
// 平台维度对比行
// ============================================
interface PlatformCompareRowProps {
  persona: PlatformPersona;
}

function PlatformCompareRow({ persona }: PlatformCompareRowProps) {
  const meta = PLATFORM_META[persona.platform];
  const dominantMeta = SEGMENT_META[persona.dominantSegment];
  const total = persona.followers;

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-2.5">
        {/* 平台标识 */}
        <div className="flex-shrink-0 w-20">
          <div className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: meta.color }}
            />
            <span className="text-[11px] text-gray-200 truncate">{meta.label}</span>
          </div>
          <div className="text-[9px] font-mono text-gray-500 mt-0.5">
            {formatNumber(total)} 用户
          </div>
        </div>

        {/* 分群分布条形图 */}
        <div className="flex-1 h-5 rounded overflow-hidden flex bg-white/[0.02]">
          {SEGMENT_ORDER.map((t) => {
            const cnt = persona.distribution[t];
            if (cnt <= 0) return null;
            const w = total > 0 ? (cnt / total) * 100 : 0;
            const segMeta = SEGMENT_META[t];
            return (
              <div
                key={t}
                className="h-full flex items-center justify-center"
                style={{ width: `${w}%`, background: segMeta.color }}
                title={`${segMeta.shortLabel}: ${formatNumber(cnt)} (${formatPercent(w)})`}
              >
                {w > 12 && (
                  <span className="text-[8px] font-mono text-black/70 font-semibold">
                    {Math.round(w)}%
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* 主导分群 */}
        <div className="flex-shrink-0 w-20 text-right">
          <div className="text-[8px] font-mono text-gray-600 uppercase tracking-wider">主导分群</div>
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <dominantMeta.icon size={10} style={{ color: dominantMeta.color }} />
            <span className="text-[10px] font-mono" style={{ color: dominantMeta.color }}>
              {dominantMeta.shortLabel}
            </span>
          </div>
        </div>

        {/* 评分 */}
        <div className="flex-shrink-0 w-28 grid grid-cols-3 gap-1">
          <ScoreCell label="活跃" value={persona.scores.activity} color="#34D399" />
          <ScoreCell label="互动" value={persona.scores.interaction} color="#A78BFA" />
          <ScoreCell label="风险" value={persona.scores.churnRisk} color="#F87171" isPercent />
        </div>
      </div>
    </div>
  );
}

// ============================================
// 平台评分小格子
// ============================================
interface ScoreCellProps {
  label: string;
  value: number;
  color: string;
  isPercent?: boolean;
}

function ScoreCell({ label, value, color, isPercent }: ScoreCellProps) {
  return (
    <div className="text-center">
      <div className="text-[8px] font-mono text-gray-600 uppercase">{label}</div>
      <div className="text-[10px] font-mono font-semibold" style={{ color }}>
        {isPercent ? `${Math.round(value)}` : value.toFixed(2)}
      </div>
    </div>
  );
}
